import React, { useContext, useCallback } from 'react';
import SwipeSlider from '@wix/da-shared-react/pkg/SwipeSlider.v2';
import { useTranslation } from 'react-i18next';
import { PapiGallection } from '@wix/da-papi-types';
import { Url, USER_PROFILE_SHOP_SUB_PATHS } from '@wix/da-url';
import { MeasuredSlider as Slider } from '@wix/da-shared-react/pkg/Slider';
import { getCurrentUserHasCore } from '@wix/da-shared-react/pkg/publicSession/selectors';
import {
  BiContextProvidedValue,
  BiData,
  DefaultBiEvent,
} from '@wix/da-bi/pkg/events';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import AddItemCard from '../../_partials/AddItemCard';
import WidgetBox, {
  Props as WidgetBoxProps,
} from '../../../../UserWidgets/_partials/WidgetBox';
import { ShopItemType } from '../../../../../../types/shop';
import { useSelector, useDispatch } from 'react-redux';
import GetCore from '../../_partials/GetCore';
import UploadMore from '../../_partials/UploadMore';
import PremiumFoldersEmptyState from '../../_partials/PremiumFoldersEmptyState';
import ShopFolderThumb, {
  DEFAULT_THUMB_HEIGHT,
  PANEL_HEIGHT,
} from '@wix/da-shared-react/pkg/DeviationViews/Thumb/ShopFolder';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import PremiumFolderCreateButton from '../../_partials/PremiumFolderCreateButton/PremiumFolderCreateButton';
import { ModalType } from '@wix/da-shared-react/pkg/Modals/redux/types';
import { pushModal } from '@wix/da-shared-react/pkg/Modals/redux/actionCreators';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import FolderShopCard from '@wix/da-shared-react/pkg/ShopCard/FolderShopCard';
import PremiumFolderSliderMobile from './PremiumFolderSliderMobile';
import s from './PremiumFolderSlider.scss';

export interface Props {
  streamId: string;
  moduleId: number;
  folders: PapiGallection[];
  total: number;
  isOwner: boolean;
  username: string;
}

export const PremiumFolderSlider: React.FC<Props & WidgetBoxProps> = ({
  widgetMoveUp,
  widgetMoveDown,
  widgetRemove,
  moduleId,
  folders,
  total,
  username,
  section,
  zone,
  isOwner,
  headerClassName,
}) => {
  const { t } = useTranslation();
  const is_shop_card = useFlag('shop_card');
  const isMobile = useContext(MobileContext);
  const isEmpty = !folders.length;
  const dispatch = useDispatch();
  const showMediaManager = useCallback(
    () =>
      dispatch(
        pushModal(ModalType.CREATE_PREMIUM_FOLDER, {
          wrapInModal: false,
        })
      ),
    [dispatch]
  );

  const hasCoreMembership = useSelector(getCurrentUserHasCore);

  // show Add New button to owners only who already have CORE
  const showAddItemCard = isOwner;

  const showAddNew =
    !showAddItemCard && hasCoreMembership && isOwner && !isMobile;
  // show GetCore to owner who doesnt have CORE yet.
  const showGetCore = !showAddItemCard && !hasCoreMembership && isOwner;

  // dont show empty state - https://phrac.deviantart.net/T57297 & https://phrac.deviantart.net/T57418
  if (isEmpty && !isOwner) {
    return null;
  }

  return (
    <WidgetBox
      section={section}
      zone={zone}
      header={t('common.premiumFolders')}
      headerExtras={
        showAddNew ? (
          <PremiumFolderCreateButton className={s['add-new']} />
        ) : showGetCore ? (
          <GetCore />
        ) : undefined
      }
      count={total}
      moduleId={moduleId}
      widgetMoveUp={widgetMoveUp}
      widgetMoveDown={widgetMoveDown}
      widgetRemove={widgetRemove}
      isEmpty={isEmpty}
      hasMore={!isEmpty} // until we implement inner slider overflow recognition
      emptyView={<PremiumFoldersEmptyState view={'wide'} />}
      seeAllUrl={Url.userShopPath(
        username,
        USER_PROFILE_SHOP_SUB_PATHS.PREMIUM_FOLDERS
      )}
      headerClassName={headerClassName}
    >
      <BiLoggerContextProvider value={{ sectionname: 'premium_folders' }}>
        {isMobile ? (
          is_shop_card ? (
            <PremiumFolderSliderMobile
              folders={folders}
              showAddItemCard={showAddItemCard}
            />
          ) : (
            <SwipeSlider startSlide={showAddItemCard ? 1 : 0}>
              {showAddItemCard && (
                <UploadMore
                  itemType={ShopItemType.PREMIUM_FOLDERS}
                  height={PANEL_HEIGHT + DEFAULT_THUMB_HEIGHT}
                  biData={BiData<DefaultBiEvent>({
                    evid: 2,
                    click_info: 'create_premium_gallery',
                    sectionname: BiContextProvidedValue,
                  })}
                />
              )}
              {folders.map(folder => (
                <ShopFolderThumb key={folder.folderId} folder={folder} />
              ))}
            </SwipeSlider>
          )
        ) : (
          <>
            <Slider
              renderSliderItems={elementSize => (
                <>
                  {showAddItemCard && (
                    <AddItemCard
                      title={t('pages.shop.premiumFolders.addItem.title')}
                      subtitle={t('pages.shop.premiumFolders.addItem.subtitle')}
                      onClick={showMediaManager}
                      nonCoreMembershipView={
                        <PremiumFoldersEmptyState view={'small'} />
                      }
                      biData={BiData<DefaultBiEvent>({
                        evid: 2,
                        click_info: 'create_premium_gallery',
                        sectionname: BiContextProvidedValue,
                      })}
                      view={'small'}
                    />
                  )}
                  {folders.map(folder =>
                    is_shop_card ? (
                      <FolderShopCard
                        key={folder.folderId}
                        folder={folder}
                        width={elementSize.width}
                        withAuthorDetails={false}
                        withIndicators={false}
                      />
                    ) : (
                      <ShopFolderThumb
                        key={folder.folderId}
                        folder={folder}
                        width={elementSize.width}
                      />
                    )
                  )}
                </>
              )}
            />
          </>
        )}
      </BiLoggerContextProvider>
    </WidgetBox>
  );
};
PremiumFolderSlider.displayName = 'PremiumFolderSlider';

export default PremiumFolderSlider;
